import React from 'react';
import { AwardedRoutine } from '../../types/awards';

// Print template interfaces
export interface PrintColumn {
  key: string;
  label: string;
  width: string; // CSS width value (e.g., "120px", "15%")
  align?: 'left' | 'center' | 'right';
  formatter?: (routine: AwardedRoutine, showScores: boolean) => string | React.ReactNode;
  className?: string;
  printClassName?: string;
}

export interface PrintSection {
  id: string;
  title: string;
  routines: AwardedRoutine[];
  columns: PrintColumn[];
  showHeader?: boolean;
  headerClassName?: string;
}

export interface PrintTemplateProps {
  sections: PrintSection[];
  reportTitle: string;
  competitionName: string;
  showScores: boolean;
  generatedAt: Date;
  stageFilter?: string;
  className?: string;
}

// Default column formatters
export const defaultColumnFormatters = {
  placement: (routine: AwardedRoutine) => routine.placement?.replace(/\D/g, '') || '',
  
  routineNumber: (routine: AwardedRoutine) => routine.routineId,
  
  studioName: (routine: AwardedRoutine) => routine.studio,
  
  routineName: (routine: AwardedRoutine) => {
    const name = routine.routineName;
    const performer = routine.performer;
    
    // Add performer name for solos if not already included
    if (performer && performer.trim() !== '' && !name.includes(performer) && routine.category === 'Solo') {
      return `${name} (${performer})`;
    }
    return name;
  },
  
  score: (routine: AwardedRoutine, showScores: boolean) => {
    if (routine.award === 'Not Scored') {
      return 'Not Scored';
    }
    
    if (!showScores) {
      return routine.award;
    }
    
    // Show award with score and drop scores
    const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
      ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
      : '';
    
    return `${routine.award} (${routine.finalScore.toFixed(0)}${dropScoresText})`;
  },
  
  ageGroup: (routine: AwardedRoutine) => routine.ageGroup,
  
  category: (routine: AwardedRoutine) => routine.category,
  
  division: (routine: AwardedRoutine) => routine.division,
};

// Print template component
const PrintTemplate: React.FC<PrintTemplateProps> = ({
  sections,
  reportTitle,
  competitionName,
  showScores,
  generatedAt,
  stageFilter,
  className = ''
}) => {
  return (
    <div className={`print-template ${className}`}>
      {/* Print Header */}
      <div className="print-header">
        <h1 className="print-title">{reportTitle}</h1>
        <h2 className="print-competition">{competitionName}</h2>
        <div className="print-meta">
          <span>Generated on {generatedAt.toLocaleDateString()} at {generatedAt.toLocaleTimeString()}</span>
          {stageFilter && <span> • {stageFilter}</span>}
          {showScores && <span> • Scores Visible</span>}
        </div>
      </div>

      {/* Print Sections */}
      <div className="print-sections">
        {sections.map((section, sectionIndex) => (
          <div key={section.id} className="print-section">
            {/* Section Header */}
            {section.showHeader !== false && (
              <div className={`print-section-header ${section.headerClassName || ''}`}>
                <h3>{section.title}</h3>
              </div>
            )}

            {/* Section Table */}
            <table className="print-table">
              <colgroup>
                {section.columns.map((column) => (
                  <col key={column.key} style={{ width: column.width }} />
                ))}
              </colgroup>
              
              <thead>
                <tr>
                  {section.columns.map((column) => (
                    <th 
                      key={column.key}
                      className={`print-th ${column.className || ''} ${column.printClassName || ''}`}
                      style={{ textAlign: column.align || 'left' }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              
              <tbody>
                {section.routines.map((routine, routineIndex) => {
                  // Determine row styling based on placement or index
                  const isAlternate = routine.placement 
                    ? (routine.rank % 2 === 0) // Placement-based alternating
                    : (routineIndex % 2 === 1); // Index-based alternating
                  
                  return (
                    <tr 
                      key={routine.routineId}
                      className={`print-tr ${isAlternate ? 'print-tr-alternate' : 'print-tr-primary'}`}
                    >
                      {section.columns.map((column) => (
                        <td 
                          key={column.key}
                          className={`print-td ${column.className || ''} ${column.printClassName || ''}`}
                          style={{ textAlign: column.align || 'left' }}
                        >
                          {column.formatter 
                            ? column.formatter(routine, showScores)
                            : (routine as any)[column.key] || ''
                          }
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrintTemplate;
